/**
 * `v1/output/events` (tenant-wide) and `v1/output/aircraft/{sn}/events`
 * (per-aircraft, NID jobs only) — ticket 86e2jbaqr Parts 3–7.
 *
 * Replaces `legacy/eventsAlerts.ts`'s `parseDocEvent`/`parseDocAlert` for
 * tenants on the `output` schema. The legacy parser stays untouched — it
 * is still correct for `airmarket`/`sait`, which haven't cut over.
 *
 * Built against real sandbox traffic (2026-08-07), not the doc alone —
 * the doc and the ticket disagree on the tenant field name, and the wire
 * disagrees with both:
 *
 *   - Ticket (Parts 3, 5, 6, 7): "tenantId → sourceTenant"
 *   - Doc: field is `tenantId`. Separately, the doc defines `sourceTenant`
 *     as the partner-drone provenance marker on aircraft topics
 *     (telemetry/networkid) — "identifies partner tenant", distinct from
 *     the event schema's own `tenantId`.
 *   - Wire: BOTH are present, and they are NOT interchangeable. `tenantId`
 *     is on every sample and is constant ("sandbox") — it looks like the
 *     receiving broker's own tenant, not per-event data. `sourceTenant` is
 *     present only on `OI`/`System` category events (from the UTM job)
 *     and absent on `TISB` category events (a different job hasn't added
 *     it yet). Both currently read "sandbox" when present, because
 *     sandbox has no partner drones to prove the distinction with — but
 *     `sourceTenant` is the field that will differ from our own tenant
 *     once a partner aircraft's event arrives, exactly like
 *     `TelemetryEnvelope.partner_tenant`. `tenantId` is kept nowhere on
 *     the envelope; `sourceTenant` is kept on `source_tenant`, verbatim,
 *     including null when the producing job hasn't added it yet — that
 *     absence is itself informative (which job emitted this).
 *
 *   - `category: "TISB"` appears on the wire (CIFIB rebroadcast) and
 *     `"Operational Assets"` (am-nid-dd-job) — both are now first-class
 *     `EventCategory`/`AlertCategory` values per the BE job/category
 *     mapping (2026-08-13), no longer folded into 'System'.
 *
 *   - `contexts` (e.g. `["drones", "surveillance"]`) is the BE's own
 *     nav-context routing hint — present on every fixture sample. Mapped
 *     via `mapBeContexts()` and read directly by
 *     `features/alerts/routing.ts`, replacing the old category-only
 *     guess. Jobs that don't set it yet (am-nid-job, am-nid-dd-job) fall
 *     back to that guess automatically — see `routing.ts`.
 *
 *   - `severity: "error"` appears on the wire; `EventSeverity` is only
 *     crit/warn/info/ok. Every "error" sample observed so far described
 *     a recoverable condition ("RTM API rejected telemetry frame; flight
 *     continues") rather than a safety emergency, so it maps to 'warn'
 *     — NOT 'crit'. This is a judgement call, not a documented mapping;
 *     flag to Maykon if "error" is meant to carry more weight than that.
 *
 *   - `droneIds: [""]` (a one-element array holding an empty string, not
 *     an empty array) is how tenant-wide events without a specific
 *     aircraft are marked. Treated the same as `droneIds: []`.
 *
 * What's deliberately NOT modeled: the observed `eventType` values
 * (`tisb_forwarded`, `utm.error`, `utm.command.invalid_type`) don't
 * appear in the doc's Appendix A event-type catalog, and two of the
 * three look like backend-internal diagnostics (a rejected telemetry
 * frame, an invalid command type) rather than the curated operational
 * events Appendix A describes. `data` is carried nowhere except the
 * three DAA fields the schema already supports — no new UI has been
 * built for arbitrary event `data`, so none is added here. If the
 * backend intends these to reach the Events tab as user-facing
 * messages, `message` already does the job; the raw `data` blob (which
 * on `utm.error` includes a full nested telemetry snapshot) is not
 * something any screen reads today.
 */
import { mapBeContexts } from '@/lib/mqtt/contextMap';
import { AlertCategory, AlertEnvelope, type AlertSeverity } from '@/lib/mqtt/schemas/alerts';
import { EventEnvelope } from '@/lib/mqtt/schemas/events';
import { asBool, asNumber, asText, recordAccepted, recordParseFailure } from './registry';

const EVENT_KIND = 'output-events';
const ALERT_KIND = 'output-alerts';

const ALLOWED_EVENT_CATEGORIES = [
  'System',
  'Mission',
  'Power',
  'Comms',
  'DAA',
  'Surveillance',
  'Infrastructure',
  'OI',
  'TISB',
  'Operational Assets',
] as const;

function normEventCategory(c: unknown): EventEnvelope['category'] {
  const s = asText(c);
  if (!s) return 'System';
  const mapped = s === 'C2' ? 'Comms' : s;
  return (ALLOWED_EVENT_CATEGORIES as readonly string[]).includes(mapped)
    ? (mapped as EventEnvelope['category'])
    : 'System'; // anything outside the known category set
}

/** Same normalisation as `normEventCategory`, against the alert enum
 *  (which additionally keeps `C2` distinct from `Comms`). Alerts used to
 *  cast `p.category` straight to `AlertCategory` with no check — an
 *  unrecognised category didn't just lose information, it failed the
 *  final `safeParse` and dropped the whole alert. */
function normAlertCategory(c: unknown): AlertCategory {
  const s = asText(c);
  if (!s) return 'System';
  return (AlertCategory.options as readonly string[]).includes(s) ? (s as AlertCategory) : 'System';
}

function normEventSeverity(s: unknown): EventEnvelope['severity'] {
  const v = asText(s)?.toLowerCase();
  const map: Record<string, EventEnvelope['severity']> = {
    critical: 'crit',
    crit: 'crit',
    warning: 'warn',
    warn: 'warn',
    // Observed on the wire, not in the doc's severity enum. See file
    // header — mapped to 'warn' based on sample messages, not a spec.
    error: 'warn',
    info: 'info',
    ok: 'ok',
  };
  return (v != null ? map[v] : undefined) ?? 'info';
}

function normAlertSeverity(s: unknown): AlertSeverity {
  const v = asText(s)?.toLowerCase();
  const map: Record<string, AlertSeverity> = {
    critical: 'critical',
    crit: 'critical',
    warning: 'warning',
    warn: 'warning',
    error: 'warning',
    info: 'info',
    ok: 'info',
  };
  return (v != null ? map[v] : undefined) ?? 'info';
}

/** `droneIds: [""]` and `droneIds: []` both mean "no specific aircraft" —
 *  observed on tenant-wide events (e.g. TISB forwarding status). */
function firstDroneId(v: unknown): string | null {
  if (!Array.isArray(v)) return null;
  for (const id of v) {
    const t = asText(id);
    if (t) return t;
  }
  return null;
}

/** Partner-provenance field — see file header. Deliberately does NOT fall
 *  back to `tenantId`: that field is the receiving broker's own tenant
 *  (constant), not per-event data, so treating it as a substitute would
 *  quietly manufacture provenance the event never carried. */
function sourceTenant(p: Record<string, unknown>): string | null {
  return asText(p.sourceTenant);
}

export function outputEventToEnvelope(raw: unknown): EventEnvelope | null {
  if (raw == null || typeof raw !== 'object') {
    recordParseFailure(EVENT_KIND, 'payload is not an object');
    return null;
  }
  const p = raw as Record<string, unknown>;

  const ts = asNumber(p.ts);
  const message = asText(p.message);
  if (ts == null || !message) {
    recordParseFailure(EVENT_KIND, 'missing ts / message');
    return null;
  }

  const droneId = firstDroneId(p.droneIds);
  const eventType = asText(p.eventType) ?? 'event';
  const data = (p.data ?? {}) as Record<string, unknown>;
  const distM = asNumber(data.distM);

  const built: EventEnvelope = {
    // Uniqueness plumbing only — falls back to eventType/ts when there is
    // no droneId, unrelated to the source_tenant question above.
    event_id: `${droneId ?? 'tenant'}:${ts}:${eventType}`,
    drone_id: droneId,
    timestamp: new Date(ts).toISOString(),
    category: normEventCategory(p.category),
    severity: normEventSeverity(p.severity),
    message,
    conflict_target:
      (typeof data.targetCallsign === 'string' && data.targetCallsign) ||
      (typeof data.targetHex === 'string' && data.targetHex) ||
      null,
    ttc_sec: null,
    dist_km: distM != null ? distM / 1000 : null,
    ack_required: false,
    acknowledged: false,
    source_tenant: sourceTenant(p),
    contexts: mapBeContexts(p.contexts),
  };

  const validated = EventEnvelope.safeParse(built);
  if (!validated.success) {
    recordParseFailure(EVENT_KIND, validated.error.issues[0]?.message ?? 'schema rejected');
    return null;
  }
  recordAccepted(EVENT_KIND);
  return validated.data;
}

function asNonNegInt(v: unknown): number | null {
  const n = asNumber(v);
  return n != null && Number.isInteger(n) && n >= 0 ? n : null;
}

/** `reachedWaypoints` (ENG-211) → typed entries. Malformed entries are dropped
 *  one by one rather than rejecting the whole alert; `reachedAt` is passed
 *  through `Date` so BE's microsecond timestamps become plain ISO. Null when the
 *  field is absent (every alert other than `waypoint_approached`). */
function asReachedWaypoints(v: unknown):
  | {
      waypoint_index: number;
      lap: number;
      reached_at: string;
      distance_m: number | null;
      missed: boolean | null;
    }[]
  | null {
  if (!Array.isArray(v)) return null;
  const out: {
    waypoint_index: number;
    lap: number;
    reached_at: string;
    distance_m: number | null;
    missed: boolean | null;
  }[] = [];
  for (const e of v) {
    if (e == null || typeof e !== 'object') continue;
    const r = e as Record<string, unknown>;
    const idx = asNonNegInt(r.waypointIndex);
    const lap = asNonNegInt(r.lap);
    const t = typeof r.reachedAt === 'string' ? Date.parse(r.reachedAt) : Number.NaN;
    if (idx == null || lap == null || Number.isNaN(t)) continue;
    out.push({
      waypoint_index: idx,
      lap,
      reached_at: new Date(t).toISOString(),
      distance_m: asNumber(r.distanceMeters),
      missed: asBool(r.missed),
    });
  }
  return out;
}

export function outputAlertToEnvelope(raw: unknown): AlertEnvelope | null {
  if (raw == null || typeof raw !== 'object') {
    recordParseFailure(ALERT_KIND, 'payload is not an object');
    return null;
  }
  const p = raw as Record<string, unknown>;

  const ts = asNumber(p.ts);
  const message = asText(p.message);
  if (ts == null || !message) {
    recordParseFailure(ALERT_KIND, 'missing ts / message');
    return null;
  }

  const droneId = firstDroneId(p.droneIds);
  const alertId = asText(p.alertId) ?? `${droneId ?? 'tenant'}:${ts}`;

  const built: AlertEnvelope = {
    alert_id: alertId,
    drone_id: droneId,
    timestamp: new Date(ts).toISOString(),
    severity: normAlertSeverity(p.severity),
    category: normAlertCategory(p.category),
    title: asText(p.title) ?? asText(p.category) ?? 'Alert',
    message,
    source_event: asText(p.sourceEvent),
    requires_ack: Boolean(p.requiresAck),
    acked_at: null,
    acked_by: null,
    source_tenant: sourceTenant(p),
    contexts: mapBeContexts(p.contexts),
    // ENG-86 `waypoint_pred` fields — top-level on the wire, not nested
    // under `data` (confirmed against Maykon's captured payload). Left
    // null on every other alert, including `waypoint_approached`.
    waypoint_index: asNumber(p.waypointIndex),
    distance_m: asNumber(p.distanceMeters),
    eta_seconds: asNumber(p.etaSeconds),
    current_lat: asNumber(p.currentLat),
    current_lng: asNumber(p.currentLon),
    target_lat: asNumber(p.targetLat),
    target_lng: asNumber(p.targetLon),
    lap: asNonNegInt(p.lap),
    reached_waypoints: asReachedWaypoints(p.reachedWaypoints),
    missed: asBool(p.missed),
    missed_threshold_m: asNumber(p.missedThresholdMeters),
  };

  const validated = AlertEnvelope.safeParse(built);
  if (!validated.success) {
    recordParseFailure(ALERT_KIND, validated.error.issues[0]?.message ?? 'schema rejected');
    return null;
  }
  recordAccepted(ALERT_KIND);
  return validated.data;
}
